import React, { memo, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom/";
import { formats, validators, dates, objects } from "investira.sdk";
import services from "../services";

// Context
const IndicadoresContext = React.createContext();

const TODAY = new Date();
const DATA_INICIO = dates.addYears(dates.toSqlDate(TODAY), -5);
const CURRENT_YEAR = TODAY.getFullYear();
const FIRST_DATE_YEAR = new Date(CURRENT_YEAR, 0, 1);

const initialState = {
  dataInicio: DATA_INICIO,
  dataFim: TODAY,
  indicador: {
    current: {},
    historico: [],
  },
  showPrecise: true,
  isPageLoading: true,
};

// Reducers
const reducers = (state = initialState, action) => {
  if (action.type === "INDICADOR_CHANGED") {
    return {
      ...state,
      indicador: {
        ...state.indicador,
        ...action.payload,
      },
    };
  }

  if (action.type === "SHOW_PRECISE_CHANGED") {
    return {
      ...state,
      showPrecise: action.payload,
    };
  }

  if (action.type === "IS_PAGE_LOADING_CHANGED") {
    return {
      ...state,
      isPageLoading: action.payload,
    };
  }
};

// Dispatchs
const acIndicadorChanged = (pObjIndicador) => ({
  type: "INDICADOR_CHANGED",
  payload: pObjIndicador,
});

const acShowPreciseChanged = (pBool) => ({
  type: "SHOW_PRECISE_CHANGED",
  payload: pBool,
});

const acIsPageLoadingChanged = (pBool) => ({
  type: "IS_PAGE_LOADING_CHANGED",
  payload: pBool,
});

// Custom Hook
export const useIndicadoresContext = () => React.useContext(IndicadoresContext);

export const IndicadoresProvider = memo(({ children }) => {
  const params = useParams();
  const [state, dispatch] = React.useReducer(reducers, initialState);

  const handleShowPreciseChange = (pValue) => {
    dispatch(acShowPreciseChanged(pValue));
  };

  const handleIndicadorChange = (pValue) => {
    dispatch(acIndicadorChanged(pValue));
  };

  const formatNumber = (
    pValue = null,
    pShowPrecise = true,
    pShowCurrency = true
  ) => {
    if (validators.isNull(pValue) || isNaN(pValue)) {
      return "";
    }

    if (!pShowPrecise) {
      return formats.formatNumber(pValue, 2, true, pShowCurrency);
    }

    return formats.friendlyNumber(pValue || 0, 2, pShowCurrency, "mi");
  };

  const filtrarPorPeriodo = (pDados, pMeses, pDataRef = dates.toDate()) => {
    const xDataLimite = dates.toSqlDate(dates.addMonths(pDataRef, pMeses));

    return pDados.filter(
      (xItem) => dates.toSqlDate(new Date(xItem.data)) >= xDataLimite
    );
  };

  const filtrarPorData = (pDados, pDataEspecifica) => {
    const xData = new Date(pDataEspecifica);
    return pDados.filter((xItem) => new Date(xItem.data) >= xData);
  };

  const calcularVariacaoPercentual = (valorInicial, valorFinal) => {
    if (valorInicial === 0) {
      return valorFinal === 0 ? 0 : Infinity;
    }

    return (valorInicial / valorFinal - 1) * 100;
  };

  const calcularDiffNoPeriodo = (
    pDadosFiltrados,
    pKey = "cotacao_ajustada"
  ) => {
    if (pDadosFiltrados.length < 2) return null;

    const cotacaoInicial = pDadosFiltrados[pDadosFiltrados.length - 1][pKey];
    const cotacaoFinal = pDadosFiltrados[0][pKey];

    const xResult = calcularVariacaoPercentual(cotacaoInicial, cotacaoFinal);

    if (isNaN(xResult)) {
      return null;
    }

    return xResult;
  };

  function adicionarRentabilidade(pData) {
    const xData = objects.deepCopy([...pData]);
    if (xData.length < 2) {
      return pData;
    }

    let xRentabilidade = 1;
    const xVariacaoAcumulada = xData[0].variacao_acumulada;

    // O primeiro elemento não tem rentabilidade calculável, então definimos como n0
    xData[0].rentabilidade = 0;

    for (let i = 1; i < xData.length; i++) {
      //xRentabilidade = xRentabilidade * xData[i].variacao_diaria;
      // Adiciona o novo atributo 'rentabilidade' ao objeto existente
      //xData[i].rentabilidade = (xRentabilidade - 1) * 100;
      pData[i].rentabilidade =
        (pData[i].variacao_acumulada / xVariacaoAcumulada - 1) * 100;
    }

    return xData;
  }

  const createHistoric = useCallback((pData, pDataCorte) => {
    let xData = pData;

    if (!validators.isNull(pDataCorte)) {
      xData = filtrarPorData(xData, pDataCorte);
    }

    const xHistorico = {
      ytd: adicionarRentabilidade(filtrarPorData(xData, FIRST_DATE_YEAR)),
      umMes: adicionarRentabilidade(filtrarPorPeriodo(xData, -1)),
      seisMeses: adicionarRentabilidade(filtrarPorPeriodo(xData, -6)),
      umAno: adicionarRentabilidade(filtrarPorPeriodo(xData, -12)),
      doisAnos: adicionarRentabilidade(filtrarPorPeriodo(xData, -24)),
      tresAnos: adicionarRentabilidade(filtrarPorPeriodo(xData, -36)),
      quatroAnos: adicionarRentabilidade(filtrarPorPeriodo(xData, -48)),
      cincoAnos: adicionarRentabilidade(filtrarPorPeriodo(xData, -60)),
      full: adicionarRentabilidade(xData),
    };

    return xHistorico;
  }, []);

  // Request indicador
  useEffect(() => {
    if (params.id) {
      dispatch(acIsPageLoadingChanged(true));
      services.indicadores.list(
        {
          params: { indicador_id: params.id },
        },
        (rRes) => {
          const xData = rRes.data;
          handleIndicadorChange({
            current: xData[0],
          });
        },
        (rErr) => {
          console.log(rErr);
        }
        // () => {
        //   dispatch(acIsPageLoadingChanged(false));
        // }
      );

      services.indicadores.cotacao(
        {
          indicador_id: params.id,
          data_inicio: state.dataInicio,
          data_fim: state.dataFim,
          cotacao_tipo_id: "1",
          //TODO: Usar 1,3,4 e depois filtrar
        },
        (rRes) => {
          const xData = rRes.data;
          const xHistoric = createHistoric(xData, DATA_INICIO);

          handleIndicadorChange({
            historico: xHistoric,
          });
        },
        (rErr) => {
          console.log(rErr);
        },
        () => {
          dispatch(acIsPageLoadingChanged(false));
        }
      );
    }
  }, [createHistoric, params.id, state.dataInicio, state.dataFim]);

  return (
    <IndicadoresContext.Provider
      value={{
        state,
        actions: {
          handleIndicadorChange,
          handleShowPreciseChange,
          formatNumber,
          filtrarPorPeriodo,
          createHistoric,
          calcularDiffNoPeriodo,
        },
      }}
    >
      {children}
    </IndicadoresContext.Provider>
  );
});

IndicadoresProvider.displayName = "IndicadoresProvider";

export default IndicadoresProvider;
